import { createApi } from "@reduxjs/toolkit/query/react";

import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import {
  type IDistrictValidationResponse,
  type IFindMySchoolMapLevelBoundariesParams,
  type IFindMySchoolMapLevelBoundariesResponse,
  type IFindMySchoolSchoolsParams,
  type IFindMySchoolSchoolsResponse,
  type IGeocodeAddressPublicParams,
  type IGeocodeAddressPublicResponse,
} from "./find-my-school.interface";
import customPublicFetchBase from "./public-fetch-base";


const findMySchoolApi = createApi({
  reducerPath: "findMySchoolApi",
  baseQuery: customPublicFetchBase,
  tagTypes: ["FindMySchool"],
  endpoints: (builder) => ({
    validateDistrict: builder.query<IDistrictValidationResponse | null, string>({
      query: (name) => ({
        url: "/find-my-school/district",
        method: "GET",
        params: { name },
      }),
      transformResponse: (result: IApiResponse<IDistrictValidationResponse>) => result.data,
    }),
    geocodeAddressPublic: builder.query<IGeocodeAddressPublicResponse, IGeocodeAddressPublicParams>({
      query: ({ address, minLng, minLat, maxLng, maxLat }) => ({
        url: "/find-my-school/geocode",
        method: "GET",
        params: {
          address,
          ...(minLng != null && minLat != null && maxLng != null && maxLat != null
            ? { minLng, minLat, maxLng, maxLat }
            : {}),
        },
      }),
      transformResponse: (result: IApiResponse<IGeocodeAddressPublicResponse>) => result.data,
    }),
    getFindMySchoolSchools: builder.query<IFindMySchoolSchoolsResponse, IFindMySchoolSchoolsParams>({
      query: ({ districtId, lat, lng, category }) => ({
        url: "/find-my-school/schools",
        method: "GET",
        params: {
          districtId,
          ...(lat != null && lng != null ? { lat, lng } : {}),
          ...(category && { category }),
        },
      }),
      transformResponse: (result: IApiResponse<IFindMySchoolSchoolsResponse>) => result.data,
    }),
    getFindMySchoolMapLevelBoundaries: builder.query<
      IFindMySchoolMapLevelBoundariesResponse,
      IFindMySchoolMapLevelBoundariesParams
    >({
      query: ({ districtId, lat, lng }) => ({
        url: "/find-my-school/map-level-boundaries",
        method: "GET",
        params: {
          districtId,
          ...(lat != null && lng != null ? { lat, lng } : {}),
        },
      }),
      transformResponse: (result: IApiResponse<IFindMySchoolMapLevelBoundariesResponse>) => result.data,
    }),
  }),
});

export const {
  useValidateDistrictQuery,
  useLazyValidateDistrictQuery,
  useGeocodeAddressPublicQuery,
  useLazyGeocodeAddressPublicQuery,
  useGetFindMySchoolSchoolsQuery,
  useLazyGetFindMySchoolSchoolsQuery,
  useGetFindMySchoolMapLevelBoundariesQuery,
  useLazyGetFindMySchoolMapLevelBoundariesQuery,
} = findMySchoolApi;

export default findMySchoolApi;
